import React, { Component } from 'react';
import { Checkbox, TextField, Button, CircularProgress, Switch } from '@material-ui/core';
import PropTypes from 'prop-types';

// Components
import InfoButton from '../../components/InfoButton';
import ConfirmationModal from '../../components/ConfirmationModal';
import PageHeader from '../../components/PageHeader';
import BrandSettings from '../../components/BrandSettings';

// Constants
import { T } from '../../constants/tokens';

// Redux
import { connect } from 'react-redux';
import { setSuccess, setAlert } from '../../redux/actions/alertActions';
import { checkSetupMode, turnOffSetupMode, turnOnSetupMode } from '../../redux/actions/setupModeActions';
import { AlertTypes } from '../../constants';

// Nav
import { getAdminSettings, patchAdminSettings, getSetupMode, toggleSetupMode } from '../../api';

const SETUP_MODE_INFO = 'This field determines if the SimulAlert application is in setup mode or not. If in setup mode, you are able to make system changes. Do not turn this on unless you are prepared for a significant vulnerability in the system.';

class EditAssignment extends Component {
  constructor(props) {
    super(props);

    this.state = {
      settings: null,
      isLoading: false,
      updatedSettings: {},
      showConfirmationModal: false,
      setupMode: false,
      showSetupModeConfirmationModal: false
    };

    this.loadAdminSettings = this.loadAdminSettings.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  componentDidMount() {
    this.loadAdminSettings();

    getSetupMode().then((data) => {
      this.setState({
        setupMode: data.setupMode
      });
    }).catch(error => this.props.setError(error ? error : 'Error: Unable to retrieve setup mode.'));
  }

  loadAdminSettings() {
    getAdminSettings().then((settings) => {
      this.setState({
        settings: settings
      });
    })
      .catch(error => {
        this.props.setError(error ? error : 'Unable to retrieve settings.');
      });
  }

  onChange = (e) => {
    e.preventDefault();

    var updatedSettings = this.state.updatedSettings;
    updatedSettings[e.target.name] = this.state.settings.find(setting => setting.name === e.target.name).formType === 'Checkbox' ? (e.target.checked ? 1 : 0) : e.target.value;

    this.setState({ updatedSettings: updatedSettings });
  };

  onSetupModeChange = (e) => {
    e.preventDefault();
    this.setState({ showSetupModeConfirmationModal: true });
  }

  toggleSetupMode = () => {
    const setupMode = !this.state.setupMode;
    this.setState({ isLoading: true });
    toggleSetupMode(setupMode).then(() => {
      this.props.setSuccess('Successfully turned setup mode ' + (setupMode ? 'ON' : 'OFF'));
      this.setState({ isLoading: false, showSetupModeConfirmationModal: false, setupMode });
      if (setupMode) this.props.turnOnSetupMode();
      else this.props.turnOffSetupMode();
    }).catch(error => {
      this.props.setError(error ? error : 'Error: Unable to turn setup mode ' + (setupMode ? 'ON' : 'OFF'));

      getSetupMode().then((data) => {
        this.setState({
          setupMode: data.setupMode,
          isLoading: false
        });
        this.props.checkSetupMode();
      }).catch(error => {
        this.props.setError(error ? error : 'Error: Unable to retrieve setup mode.');
        this.setState({ isLoading: false });
        this.props.checkSetupMode();
      });
    });
  }

  submitChanges = () => {
    this.setState({ isLoading: true });
    patchAdminSettings(this.state.updatedSettings).then((res) => {
      if (res && res.data) {
        this.props.setSuccess(res.data);
        this.loadAdminSettings();
        this.setState({ updatedSettings: {} });
      }
      else {
        this.props.setError('Something went wrong. Please try again.');
      }
      this.setState({ isLoading: false, showConfirmationModal: false, updatedSettings: {} });
    })
      .catch((error) => {
        this.props.setError(error ? error : 'Error: Unable to save assignment.');
        this.setState({ isLoading: false, showConfirmationModal: false });
      });
  }

  render() {
    if (this.state.settings == null) {
      return null;
    }

    return (
      <>
        {/* Change Setup Mode */}
        <ConfirmationModal
          open={this.state.showSetupModeConfirmationModal}
          onClose={() => this.setState({ showSetupModeConfirmationModal: false })}
          onConfirm={this.toggleSetupMode}
          color={this.props.schoolAlertStatus.color}
          title={'Are you sure you want to ' + (this.state.setupMode ? 'turn off' : 'turn on') + ' setup mode?'}
          body={this.state.setupMode ? 'You will not be able to make any more changes to the system until setup mode has turned back on. Badge statuses will start to be accepted immediately.' : 'This is a security vulnerability. Alerts will no longer be accepted by badges until setup mode is turned back off. Please be sure to turn on setup mode at an appropriate time to ensure the safety of staff and students.'}
          isLoading={this.state.isLoading}
        />

        {/* Update Settings */}
        <ConfirmationModal
          open={this.state.showConfirmationModal}
          onClose={() => this.setState({ showConfirmationModal: false })}
          onConfirm={this.submitChanges}
          color={this.props.schoolAlertStatus.color}
          title={'Are you sure you want to make these changes?'}
          isLoading={this.state.isLoading}
        />

        <PageHeader
          title='Platform settings'
          subtitle='How the system treats badges, hubs and alerts.'
          actions={
            <Button
              onClick={() => this.setState({ showConfirmationModal: true })}
              variant='contained'
              color='primary'
              disabled={this.state.isLoading || !this.props.setupMode}
            >
              Save changes
              {this.state.isLoading && <CircularProgress size={16} style={{ marginLeft: 8, color: 'inherit' }} />}
            </Button>
          }
        />

        {/* Setup mode sits above the settings it gates. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 16,
            marginBottom: 24,
            background: T.surfaceCard,
            border: '1px solid ' + T.hairline,
            borderRadius: 6
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: T.ink }}>
                Setup mode
              </span>
              <InfoButton name={'Setup Mode'} body={SETUP_MODE_INFO} />
            </div>
            <div style={{ fontSize: 13, lineHeight: '18px', color: T.body }}>
              {this.state.setupMode
                ? 'On — badge alerts are not being accepted.'
                : 'Off — badge alerts are being accepted normally.'}
            </div>
          </div>
          <Switch
            checked={this.state.setupMode}
            onChange={this.onSetupModeChange}
            name={'SETUP_MODE'}
            inputProps={{ 'aria-label': 'Setup mode' }}
          />
        </div>

        <form
          noValidate
          autoComplete='off'
          style={{
            background: T.surfaceCard,
            border: '1px solid ' + T.hairline,
            borderRadius: 6,
            overflow: 'hidden'
          }}
        >
          {this.state.settings && this.state.settings.map((setting, index) => {
            const locked = !this.state.setupMode && setting.setupModeOnly;

            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 16,
                  padding: 16,
                  borderBottom: index === this.state.settings.length - 1
                    ? 'none'
                    : '1px solid ' + T.hairlineSoft
                }}
              >
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: T.ink }}>
                      {setting.friendlyName}
                    </span>
                    <InfoButton name={setting.friendlyName} body={setting.description} />
                  </div>
                  {setting.description &&
                    <div style={{ fontSize: 13, lineHeight: '18px', color: T.body, textWrap: 'pretty' }}>
                      {setting.description}
                    </div>}
                </div>

                <div style={{ flex: '0 0 200px', display: 'flex', justifyContent: 'flex-end' }}>
                  {setting.formType === 'Checkbox' ?
                    <Checkbox
                      onChange={this.onChange}
                      name={setting.name}
                      defaultChecked={setting.value ? true : false}
                    /> :
                    <TextField
                      disabled={locked}
                      name={setting.name}
                      type={setting.formType}
                      defaultValue={setting.value}
                      fullWidth
                      onChange={this.onChange}
                      variant='outlined'
                      size='small'
                    />}
                </div>
              </div>
            );
          })}
        </form>

        {/* Super only, as the server requires. */}
        {this.props.isSuper && <BrandSettings />}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  schoolAlertStatus: state.status.schoolStatus || AlertTypes.UNKNOWN,
  isMobile: state.ui.isMobile,
  setupMode: state.setupMode.mode || state.setupMode.isLoading,
  // By name: compare as the rest of the app does, never by reference.
  isSuper: state.auth.userType?.name === 'Super'
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg),
  setSuccess: (msg) => setSuccess(dispatch, msg),
  checkSetupMode: () => checkSetupMode(dispatch),
  turnOnSetupMode: () => turnOnSetupMode(dispatch),
  turnOffSetupMode: () => turnOffSetupMode(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditAssignment);

EditAssignment.propTypes = {
  setError: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired,
  checkSetupMode: PropTypes.func.isRequired,
  turnOnSetupMode: PropTypes.func.isRequired,
  turnOffSetupMode: PropTypes.func.isRequired,
  schoolAlertStatus: PropTypes.object,
  isMobile: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  setupMode: PropTypes.bool.isRequired,
  isSuper: PropTypes.bool
};
